/**
 * QA LST-02 — Ürün detaydan geri dönünce listede kalınan yere dönme.
 *
 * Sonsuz kaydırma URL'e son yüklenen sayfayı (`?page=N`) yazar. "Geri" ile dönüldüğünde tarayıcı yalnızca o
 * sayfayı yüklediği için sayfa eskisinden kısadır ve tarayıcının kendi scroll geri yüklemesi tutmaz. Üstelik
 * tıklanan ürün çoğu zaman daha önceki bir sayfadadır (sonraki sayfa, liste sonuna gelinmeden yüklenir).
 *
 * Bu yüzden ürüne tıklanırken:
 * - kart (handle, sayfası, sayfa içindeki sırası, ekrandaki konumu) sessionStorage'a yazılır,
 * - URL tıklanan ürünün sayfasına çekilir; böylece geri dönüşte tarayıcı doğrudan o sayfayı yükler ve kart
 *   ek istek gerekmeden bulunur.
 * Listeye geri dönüldüğünde kart bulunur ve ekranda bıraktığı konuma kaydırılır. Kart yüklü sayfada değilse
 * tıklandığı sayfaya kadar "önceki ürünleri göster" tetiklenir; ürün listeden çıkmışsa aynı sıradaki karta dönülür.
 *
 * - Yalnızca geri / ileri gezinmede çalışır; listeye menüden yeniden girilirse kayıt silinir.
 * - bfcache ile geri gelinirse script yeniden çalışmaz; tarayıcı zaten her şeyi olduğu gibi geri getirir.
 * - infinite-scroll.js ile tek bağ, kartlara yazdığı `data-hp-page` (kartın geldiği sayfa) değeridir. Onun
 *   dışında `[data-collection-infinite-scroll]` kökü, `#product-list-<sectionId>` grid'i ve
 *   `.js-show-prev-products` butonu yeterlidir.
 */
(() => {
  if (window.HpCollectionScrollRestore) return;
  window.HpCollectionScrollRestore = true;

  const STORAGE_KEY = 'hp:collection-scroll-restore';
  const ROOT_SELECTOR = '[data-collection-infinite-scroll]';
  const PREV_BUTTON_SELECTOR = '.js-show-prev-products';
  const PRODUCT_LINK_SELECTOR = 'a[href*="/products/"]';
  const PRODUCT_CARD_SELECTOR = 'product-card[handle]';
  const MAX_AGE_MS = 30 * 60 * 1000;
  const MAX_PREV_LOADS = 6;
  const PREV_LOAD_TIMEOUT_MS = 8000;
  const MANAGER_INIT_DELAY_MS = 300;
  const URL_REASSERT_WINDOW_MS = 5000;
  const SETTLE_DELAYS_MS = [150, 500, 1200];

  // Listeyi belirlemeyen parametreler. `page` sonsuz kaydırmayla değişir; `_` ile başlayanlar, utm ve tıklama
  // kimlikleri takip içindir. Shopify Safari'de geri dönüşte sayfayı shop.app/accounts/sur üzerinden yönlendirip
  // URL'e `_su_rec` / `_su_rec_id` ekleyebiliyor: bunlar sayılınca liste "başka liste" görünüyor, iPhone'da geri
  // dönüş sayfa başında açılıyordu.
  const IGNORED_PARAM_PATTERN = /^(page|_.*|utm_.*|fbclid|gclid|gbraid|wbraid|msclkid|yclid|srsltid|preview_theme_id)$/i;

  function readRecord() {
    try {
      const record = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (!record || typeof record.path !== 'string' || typeof record.listKey !== 'string') return null;
      return record;
    } catch (error) {
      return null;
    }
  }

  function writeRecord(record) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch (error) {
      // sessionStorage kapalıysa özellik sessizce devre dışı kalır
    }
  }

  function clearRecord() {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // yoksay
    }
  }

  // Aynı liste = aynı yol + aynı filtre / sıralama.
  function getListKey() {
    const url = new URL(window.location.href);
    const params = new URLSearchParams();

    url.searchParams.forEach((value, key) => {
      if (!IGNORED_PARAM_PATTERN.test(key)) params.append(key, value);
    });
    params.sort();

    return `${url.pathname}?${params.toString()}`;
  }

  function getRoot() {
    return document.querySelector(ROOT_SELECTOR);
  }

  function getGrid(root = getRoot()) {
    const sectionId = root?.dataset.sectionId;
    return sectionId ? document.getElementById(`product-list-${sectionId}`) : null;
  }

  function getGridItem(grid, node) {
    let current = node;
    while (current && current.parentElement !== grid) current = current.parentElement;
    return current || null;
  }

  // Kartın kimliği ürün handle'ıdır: renk seçilince kartın linki kardeş ürüne döner ama handle değişmez.
  function getItemHandle(item) {
    const card = item.matches(PRODUCT_CARD_SELECTOR) ? item : item.querySelector(PRODUCT_CARD_SELECTOR);
    return card?.getAttribute('handle') || null;
  }

  // Handle'ı olmayan kartlar için yedek kimlik: içindeki ilk ürün linki.
  function getItemProductPath(item) {
    const link = item.matches(PRODUCT_LINK_SELECTOR) ? item : item.querySelector(PRODUCT_LINK_SELECTOR);
    if (!link) return null;

    try {
      return new URL(link.getAttribute('href'), window.location.origin).pathname;
    } catch (error) {
      return null;
    }
  }

  // `display: contents` sarmalayıcıların (ör. `xo-product`) kutusu olmaz → ilk kutulu çocuğa bakılır.
  function getItemBox(item) {
    if (item.getClientRects().length > 0) return item;
    return Array.from(item.children).find((child) => child.getClientRects().length > 0) || item;
  }

  function findItem(grid, record) {
    const items = Array.from(grid.children);

    return (
      (record.handle && items.find((item) => getItemHandle(item) === record.handle)) ||
      items.find((item) => getItemProductPath(item) === record.path) ||
      null
    );
  }

  // infinite-scroll.js her karta geldiği sayfayı yazar.
  function getItemPage(item) {
    const page = Number.parseInt(item?.dataset.hpPage, 10);
    return Number.isInteger(page) && page > 0 ? page : null;
  }

  function getPageItems(grid, page) {
    return Array.from(grid.children).filter((item) => getItemPage(item) === page);
  }

  function pointUrlToPage(page) {
    try {
      const url = new URL(window.location.href);

      if (page > 1) {
        url.searchParams.set('page', String(page));
      } else {
        url.searchParams.delete('page');
      }

      if (url.href !== window.location.href) {
        window.history.replaceState(window.history.state, '', url.href);
      }
    } catch (error) {
      // URL güncellenemezse geri dönüşte "önceki ürünleri göster" yedeği devreye girer
    }
  }

  // Tarayıcının kendi scroll geri yüklemesi kayıt geçmiş girdisine bağlıdır. Ürüne tıklanırken "manual" yapılır ki
  // geri dönüşte tarayıcı sayfa yüklenirken eski (uzun listedeki) konuma zıplamasın; konumu bu script verir.
  function setScrollRestoration(mode) {
    try {
      if ('scrollRestoration' in window.history && window.history.scrollRestoration !== mode) {
        window.history.scrollRestoration = mode;
      }
    } catch (error) {
      // yoksay
    }
  }

  function isBackForwardNavigation() {
    const entry = window.performance?.getEntriesByType?.('navigation')?.[0];
    if (entry) return entry.type === 'back_forward';

    // Navigation Timing 2 olmayan eski tarayıcılar
    return window.performance?.navigation?.type === 2;
  }

  function onDocumentClick(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest?.(PRODUCT_LINK_SELECTOR);
    if (!link || link.target === '_blank') return;

    const grid = getGrid();
    if (!grid || !grid.contains(link)) return;

    const item = getGridItem(grid, link);
    const path = item ? getItemProductPath(item) : null;
    if (!path) return;

    const page = getItemPage(item);

    writeRecord({
      listKey: getListKey(),
      handle: getItemHandle(item),
      path,
      page,
      index: page ? getPageItems(grid, page).indexOf(item) : -1,
      top: Math.round(getItemBox(item).getBoundingClientRect().top),
      ts: Date.now(),
    });

    setScrollRestoration('manual');
    if (!page) return;

    // Geri dönüşte tarayıcı son yüklenen sayfayı değil, tıklanan ürünün sayfasını yüklesin.
    pointUrlToPage(page);

    // Tıklama anında sonraki sayfa yükleniyorsa sonsuz kaydırma URL'i yeniden ileri alabilir; ayrılırken tekrar sabitlenir.
    const reassert = () => {
      pointUrlToPage(page);
      setScrollRestoration('manual');
    };
    window.addEventListener('pagehide', reassert, { once: true });
    window.setTimeout(() => window.removeEventListener('pagehide', reassert), URL_REASSERT_WINDOW_MS);
  }

  function waitForGridChange(grid, timeoutMs) {
    return new Promise((resolve) => {
      let timer = null;
      const observer = new MutationObserver(() => {
        observer.disconnect();
        window.clearTimeout(timer);
        resolve(true);
      });

      timer = window.setTimeout(() => {
        observer.disconnect();
        resolve(false);
      }, timeoutMs);

      observer.observe(grid, { childList: true });
    });
  }

  function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  function scrollToItem(item, savedTop) {
    const box = getItemBox(item);
    const viewportTop = Number.isFinite(savedTop) ? Math.min(savedTop, window.innerHeight - 80) : 120;
    let cancelled = false;

    const apply = () => {
      if (cancelled || !box.isConnected) return;
      const target = window.scrollY + box.getBoundingClientRect().top - viewportTop;
      window.scrollTo(0, Math.max(0, target));
    };

    // Kullanıcı kendi kaydırmaya başladıysa konumu zorlamayı bırak.
    const cancel = () => {
      cancelled = true;
    };
    ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((type) => {
      window.addEventListener(type, cancel, { once: true, passive: true });
    });

    // Görseller / fontlar yüklendikçe yerleşim kayabildiği için kısa süre boyunca konum tazelenir.
    apply();
    SETTLE_DELAYS_MS.forEach((delay) => window.setTimeout(apply, delay));
    if (document.readyState !== 'complete') {
      window.addEventListener('load', apply, { once: true });
    }
  }

  async function restore(record) {
    const root = getRoot();
    const grid = getGrid(root);
    if (!root || !grid) return;

    let item = findItem(grid, record);

    // Kart daha önceki bir sayfadaysa "önceki ürünleri göster" tetiklenir. Tıklandığı sayfa biliniyorsa yalnızca
    // o sayfaya kadar gidilir: ürün listeden çıkmışsa (tükendi vb.) boşuna sayfa yüklenmez.
    if (!item && root.querySelector(PREV_BUTTON_SELECTOR)) {
      await wait(MANAGER_INIT_DELAY_MS);

      for (let attempt = 0; !item && attempt < MAX_PREV_LOADS; attempt += 1) {
        const firstLoadedPage = getItemPage(grid.firstElementChild);
        if (record.page && firstLoadedPage && firstLoadedPage <= record.page) break;

        const prevButton = root.querySelector(PREV_BUTTON_SELECTOR);
        if (!prevButton || prevButton.disabled || !grid.isConnected) break;

        const changed = waitForGridChange(grid, PREV_LOAD_TIMEOUT_MS);
        prevButton.click();
        if (!(await changed)) break;

        item = findItem(grid, record);
      }
    }

    // Ürün artık listede yoksa aynı sayfada aynı sıradaki karta dönülür.
    if (!item && record.page) {
      const pageItems = getPageItems(grid, record.page);
      item = pageItems[Math.min(Math.max(record.index, 0), pageItems.length - 1)] || null;
    }

    if (item) scrollToItem(item, record.top);
  }

  document.addEventListener('click', onDocumentClick, true);

  // bfcache ile dönüldüğünde tarayıcı sayfayı olduğu gibi getirir; kullanılmayan kayıt sonraki gezinmeye
  // sarkmasın, geçmiş girdisi de tarayıcının kendi geri yüklemesine dönsün.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    clearRecord();
    setScrollRestoration('auto');
  });

  const record = readRecord();
  const isUsable =
    Boolean(record) &&
    isBackForwardNavigation() &&
    record.listKey === getListKey() &&
    Date.now() - (record.ts || 0) < MAX_AGE_MS;

  if (record) clearRecord();

  if (!isUsable) {
    // Tıklamada "manual" yapılan girdiye kayıt olmadan dönüldüyse (ör. 30 dk geçti) tarayıcıya geri ver.
    setScrollRestoration('auto');
    return;
  }

  // Tarayıcının kendi (kısa sayfada tutmayan) geri yüklemesi bizimkinin üzerine yazmasın. Yavaş bağlantıda `load`
  // geç geldiği için "auto"ya ancak sayfa tamamen yüklenip yerleşim oturduktan sonra dönülür.
  setScrollRestoration('manual');

  const releaseScrollRestoration = () => {
    window.setTimeout(() => setScrollRestoration('auto'), SETTLE_DELAYS_MS[SETTLE_DELAYS_MS.length - 1] + 300);
  };

  const run = () => {
    restore(record)
      .catch((error) => console.error('[collection-scroll-restore]', error))
      .finally(() => {
        if (document.readyState === 'complete') {
          releaseScrollRestoration();
        } else {
          window.addEventListener('load', releaseScrollRestoration, { once: true });
        }
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
